<template>
  <v-container v-if="auth.isProtoMember">
    <v-alert type="info" variant="tonal">
      Guest sessions cannot create or manage household members. Log in to an
      existing account or complete guest checkout first.
    </v-alert>
  </v-container>
  <template v-else>
    <PageHeader title="Manage Children">
      <template #actions>
        <CreateButton
          label="Add Child"
          :disabled="selfId == null"
          @click="edit(null)"
        />
      </template>
    </PageHeader>
    <v-container>
      <v-alert v-if="loadError" type="error" role="alert">
        {{ loadError }} <v-btn @click="load">Retry</v-btn>
      </v-alert>
      <v-card :loading="loading">
        <v-data-table
          :items="tableRows"
          :headers="headers"
          item-value="personId"
          hide-default-footer
        >
          <template #[`item.actions`]="{ item: child }">
            <v-btn
              v-if="!child.activeMembership"
              icon="mdi-ticket-account"
              title="Membership options"
              :to="{
                name: 'youth-memberships',
                params: { id: child.personId },
              }"
            />
            <v-btn
              icon="mdi-account-details"
              title="View profile"
              :to="{
                name: 'Person Profile',
                params: { personId: child.personId },
              }"
            />
            <v-btn
              icon="mdi-pencil"
              :title="`Edit ${child.name}`"
              @click="edit(child.personId)"
            />
          </template>
        </v-data-table>
        <v-card-text v-if="!loading && !loadError && !children.length"
          >No children on file.</v-card-text
        >
      </v-card>
    </v-container>
    <v-dialog v-model="editing" max-width="1000" scrollable persistent>
      <v-card :title="editingId == null ? 'Add Child' : 'Edit Child'">
        <v-card-text>
          <MemberProfileForm
            v-if="editing"
            :key="editingId ?? 'new'"
            :person-id="editingId ?? undefined"
            child
            tabbed
            @saved="saved"
            @cancel="editing = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </template>
</template>

<script setup lang="ts">
import CreateButton from "@/components/shared/CreateButton.vue";
import { computed, onScopeDispose, ref, shallowRef, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useMemberPersonId } from "@/composables/people/useMemberPersonId";
import { errorMessage } from "@/composables/generated/contracts";
import { useUser } from "@/composables/useUser";
import { useUtility } from "@/composables/useUtility";
import MemberProfileForm from "@/components/portal/MemberProfileForm.vue";
import { usePersonService } from "../../../cbi-generator/frontend/composables/usePersonFrontendService";
import type { PersonMember } from "../../../cbi-generator/generated/dto/PersonMember";
import type { MemberProfile } from "../../../cbi-generator/generated/dto/MemberProfile";
const auth = useAuthStore();
const selfId = useMemberPersonId();
const service = usePersonService();
const userStore = useUser();
const { formatDateNY, formatMembershipLabel } = useUtility();
const children = shallowRef<PersonMember[]>([]);
const headers = [
  { title: "Name", key: "name" },
  { title: "DOB", key: "dob" },
  { title: "Card", key: "card" },
  { title: "Membership", key: "membership" },
  { title: "Actions", key: "actions", sortable: false },
];
const tableRows = computed(() =>
  children.value.map((child) => ({
    personId: child.personId,
    name: child.name,
    dob: formatDateNY(child.dob),
    card: child.activeCardNum ? `Active (${child.activeCardNum})` : "--",
    membership: formatMembershipLabel(child) ?? "--",
    activeMembership: !!child.activeMembershipTypeName,
  })),
);
const loading = ref(false);
const loadError = ref("");
const editing = ref(false);
const editingId = ref<number | null>(null);
let version = 0;
onScopeDispose(() => ++version);
async function load() {
  const current = ++version;
  children.value = [];
  loadError.value = "";
  loading.value = false;
  if (selfId.value == null || auth.isProtoMember) return;
  loading.value = true;
  try {
    const self = await service.getPersonMember(selfId.value);
    const loaded = await self.getChildren();
    if (current === version) children.value = loaded;
  } catch (cause) {
    if (current === version) loadError.value = errorMessage(cause);
  } finally {
    if (current === version) loading.value = false;
  }
}
watch(
  [selfId, () => auth.isProtoMember],
  () => {
    editing.value = false;
    void load();
  },
  { immediate: true },
);
function edit(id: number | null) {
  editingId.value = id;
  editing.value = true;
}
function saved(profile: MemberProfile) {
  const remaining = children.value.filter(
    (p) => p.personId !== profile.person.personId,
  );
  children.value = [...remaining, profile.person];
  editing.value = false;
  const current = version;
  void userStore.loadUser(true).catch((cause) => {
    if (current === version)
      loadError.value = `Child saved, but refreshing the account summary failed: ${errorMessage(cause)}`;
  });
}
</script>
